import React, { useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { DirectUpload } from "@rails/activestorage"
import { csrfToken } from "@react/helpers/fetch"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"
import ClientSitePicker from "./ClientSitePicker"
import ManagerAutocomplete from "./ManagerAutocomplete"

const directUploadUrl = () => {
  const meta = document.querySelector('meta[name="direct-upload-url"]')
  return meta?.getAttribute("content") || "/rails/active_storage/direct_uploads"
}

const ACCEPTED_EXTENSIONS = [".csv", ".xls", ".xlsx", ".xlsm", ".ods"]

const hasAcceptedExtension = (filename) => {
  const lower = filename.toLowerCase()
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))
}

const defaultManagerId = (managers, currentUserId) =>
  managers.some(manager => String(manager.id) === String(currentUserId)) ? currentUserId : ""

const ImportFromFileModal = ({ isOpen, onClose, importUrl, managers = [], currentUserId }) => {
  const [managerId, setManagerId] = useState(() => defaultManagerId(managers, currentUserId))
  const [client, setClient]   = useState(null)
  const [site,   setSite]     = useState(null)
  const [file,   setFile]     = useState(null)
  const [signedId, setSignedId] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const fileRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return
    setClient(null); setSite(null); setFile(null); setSignedId(null)
    setManagerId(defaultManagerId(managers, currentUserId))
    setUploading(false); setSubmitting(false); setIsDragging(false)
  }, [isOpen])

  const uploadFile = (next) => {
    setFile(next)
    setSignedId(null)
    setUploading(true)
    const upload = new DirectUpload(next, directUploadUrl())
    upload.create((error, blob) => {
      setUploading(false)
      if (error) {
        flashAlert(I18n.t("quotes.import.upload_failed"))
        setFile(null)
        return
      }
      setSignedId(blob.signed_id)
    })
  }

  const handleFileChange = e => {
    const next = e.target.files?.[0]
    if (!next) return
    uploadFile(next)
  }

  const uploadRef = useRef(uploadFile)
  uploadRef.current = uploadFile

  // A file dropped outside the zone would otherwise be opened by the browser.
  useEffect(() => {
    if (!isOpen) return
    let depth = 0
    const carriesFiles = e => Array.from(e.dataTransfer?.types || []).includes("Files")

    const onEnter = e => {
      if (!carriesFiles(e)) return
      e.preventDefault()
      depth += 1
      setIsDragging(true)
    }
    const onOver = e => {
      if (!carriesFiles(e)) return
      e.preventDefault()
      e.dataTransfer.dropEffect = "copy"
    }
    const onLeave = e => {
      if (!carriesFiles(e)) return
      depth = Math.max(depth - 1, 0)
      if (depth === 0) setIsDragging(false)
    }
    const onDrop = e => {
      if (!carriesFiles(e)) return
      e.preventDefault()
      depth = 0
      setIsDragging(false)
      const dropped = e.dataTransfer.files?.[0]
      if (!dropped) return
      if (!hasAcceptedExtension(dropped.name)) {
        flashAlert(I18n.t("quotes.import.upload_failed"))
        return
      }
      uploadRef.current(dropped)
    }

    window.addEventListener("dragenter", onEnter)
    window.addEventListener("dragover",  onOver)
    window.addEventListener("dragleave", onLeave)
    window.addEventListener("drop",      onDrop)
    return () => {
      window.removeEventListener("dragenter", onEnter)
      window.removeEventListener("dragover",  onOver)
      window.removeEventListener("dragleave", onLeave)
      window.removeEventListener("drop",      onDrop)
    }
  }, [isOpen])

  const canSubmit = client && site && managerId && signedId && !uploading && !submitting

  const handleConfirm = () => {
    if (!canSubmit) return
    setSubmitting(true)
    const form = document.createElement("form")
    form.method = "post"
    form.action = importUrl
    const fields = {
      authenticity_token: csrfToken(),
      type:               "projects",
      "quote[client_id]":     client.id,
      "quote[site_id]":       site.id,
      "quote[manager_id]":    managerId,
      data_file:              signedId,
    }
    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement("input")
      input.type  = "hidden"
      input.name  = name
      input.value = String(value)
      form.appendChild(input)
    })
    document.body.appendChild(form)
    form.submit()
  }

  return (
    <>
      <Modal
        isOpen  = {isOpen}
        onClose = {onClose}
        size    = "lg"
        eyebrow = {I18n.t("quotes.project_index.import_dpgf")}
        title   = {I18n.t("quotes.project_index.import.title")}
        footer  = {
          <>
            <Button variant="ghost" onClick={onClose}>{I18n.t("button.cancel")}</Button>
            <Button variant="primary" onClick={handleConfirm} disabled={!canSubmit}>
              <FontAwesomeIcon icon={submitting ? "spinner" : "file-import"} spin={submitting} />
              {I18n.t("button.create")}
            </Button>
          </>
        }
      >
        <ClientSitePicker
          client         = {client}
          site           = {site}
          onClientChange = {setClient}
          onSiteChange   = {setSite}
        />

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700">
            {I18n.t("quotes.builder.settings.manager_label")}
          </label>
          <ManagerAutocomplete managers={managers} value={managerId} onChange={setManagerId} />
        </div>

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700">
            {I18n.t("quotes.project_index.import.dpgf_file")}
          </label>

          <div
            role         = "button"
            tabIndex     = {0}
            onClick      = {() => fileRef.current?.click()}
            onKeyDown    = {(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileRef.current?.click() } }}
            className    = {`w-full flex items-center justify-center gap-2 h-12 rounded-md border-2 border-dashed bg-white text-sm transition-colors cursor-pointer ${
              isDragging
                ? "border-brand-500 bg-brand-50 text-brand-700"
                : "border-slate-200 hover:border-brand-500 text-slate-600"
            }`}
          >
            <FontAwesomeIcon icon={uploading ? "spinner" : "file-upload"} spin={uploading} />
            <span className="truncate max-w-full pointer-events-none">
              {uploading
                ? I18n.t("quotes.project_index.import.uploading")
                : file
                  ? file.name
                  : I18n.t("quotes.project_index.import.pick_file")}
            </span>
          </div>

          <input
            ref      = {fileRef}
            type     = "file"
            accept   = ".csv,.xls,.xlsx,.xlsm,.ods"
            onChange = {handleFileChange}
            className= "hidden"
          />

          <p className="text-xs text-slate-500">{I18n.t("quotes.project_index.import.file_hint")}</p>
        </div>
      </Modal>
      {isOpen && isDragging && (
        <div className="fixed inset-0 z-[60] p-6 pointer-events-none">
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 rounded-2xl border-4 border-dashed border-brand-500 bg-brand-50/90 text-brand-700">
            <FontAwesomeIcon icon="file-arrow-up" className="text-5xl" />
            <span className="text-lg font-semibold">{I18n.t("quotes.project_index.import.drop_anywhere")}</span>
            <span className="text-sm text-brand-600">.csv, .xls, .xlsx, .xlsm, .ods</span>
          </div>
        </div>
      )}
    </>
  )
}

export default ImportFromFileModal
